import { Box, ButtonBase, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import type { SupportPriority } from "../../types.js";

/**
 * Click-to-change priority pill. Same visual language as
 * `<TransitionMenu>` (slim pill, colour dot, chevron). The priority's
 * own colour drives the dot tint.
 *
 * Caller wires `onChange(priorityId)` to a mutation that PATCHes the
 * ticket. As of Task 14, the caller routes the PATCH through the
 * dashboard server's admin proxy via `supportApi.updateAdminTicketPriority`,
 * which lands on `PATCH /api/admin/tickets/:id/priority` — the
 * dedicated staff endpoint that runs through `Tickets::UpdatePriority`
 * (history + audit + customer activity fan-out, same path the bulk
 * transition uses). The legacy `updateTicket({ priority_id })` is kept
 * for the customer-shaped surface.
 */
export function PriorityMenu({
  current,
  options,
  onChange,
  isPending,
}: {
  current: { id: number; name: string; color?: string | null } | null;
  options: SupportPriority[];
  onChange: (priorityId: number) => void;
  isPending: boolean;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: "inline-flex" }}>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change priority"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 500,
          color: "text.primary",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        {current ? (
          <>
            <Box
              component="span"
              sx={{ height: 6, width: 6, borderRadius: "50%", bgcolor: "text.disabled" }}
              style={current.color ? { backgroundColor: current.color } : undefined}
            />
            <span>{current.name}</span>
          </>
        ) : (
          <Box component="span" sx={{ fontStyle: "italic", color: "text.disabled" }}>None</Box>
        )}
        <Box component="svg" viewBox="0 0 8 8" aria-hidden sx={{ height: 8, width: 8, opacity: 0.7 }}>
          <path d="M1 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </Box>
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ role: "menu", dense: true, sx: { minWidth: 200 } }}
      >
        {options.map((p) => {
          const isCurrent = p.id === current?.id;
          return (
            <MenuItem
              key={p.id}
              role="menuitem"
              onClick={() => {
                setAnchorEl(null);
                if (!isCurrent) onChange(p.id);
              }}
              sx={[
                { gap: 1, fontSize: 12 },
                isCurrent
                  ? {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                      color: "primary.main",
                    }
                  : { color: "text.primary" },
              ]}
            >
              <Box
                component="span"
                sx={{ height: 6, width: 6, borderRadius: "50%", bgcolor: "text.disabled" }}
                style={p.color ? { backgroundColor: p.color } : undefined}
              />
              {p.name}
              {isCurrent && (
                <Box
                  component="span"
                  sx={{ ml: "auto", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.7 }}
                >
                  current
                </Box>
              )}
            </MenuItem>
          );
        })}
        {options.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 12, color: "text.disabled" }}>
            No priorities defined.
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
